import { useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { MarkupContext } from '../../providers/Markup'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid'
import ButtonEl from '../helpers/ctas/Button'
import CtaDropDown from './CTA'
import MainNav from './MainNav'
import MobileMegaMenuNav from './MobileMegaMenuNav'
import SearchForm, { useSearchForm } from './SearchForm'
import { useMemberSession, getCsrfToken, getReturnUrl } from '../../helpers/memberSession'
import { dataLayerPush } from '../../helpers/thirdparty'

const MY_PROFILE_URL = '/member'
const LOGOUT_URL = '/member/logout'

function MobileNav(props) {
  const { detectPaletteClass } = useContext(MarkupContext)
  const [menuActive, setMenuActive] = useState(false)
  const { searchTerm, setSearchTerm, submitForm } = useSearchForm()

  // Check if we have data-sourced mega menu columns
  const hasDataSourcedNav = props.megaMenuColumns?.length > 0

  const hasTopNav = props.headerDataSource?.topNavigationBar?.length > 0
  const topNavBar = props.headerDataSource?.topNavigationBar

  const joinLinkData = props.headerDataSource?.joinLink
  const loginLinkData = props.headerDataSource?.loginLink
  // My Profile URL is configurable on the Website node (Membership tab → My Profile Link),
  // same place as the Login / Join Us links. Falls back to /member when not set.
  const myProfileUrl = props.headerDataSource?.myProfileLink?.link?.url || MY_PROFILE_URL

  const { isAuthenticated, needsRenewal } = useMemberSession()

  const content = (
    <>
      <div
        className={`relative group/menu h-6 w-6 ${menuActive && (props.searchActive === false || props.searchActive === undefined) ? 'active' : 'inactive'}`}
      >
        <div className="absolute top-0 left-0">
          <Bars3Icon
            className={detectPaletteClass(
              'h-6 w-6 text-base-text group-[.active]/menu:rotate-90 group-[.active]/menu:opacity-0 group-[.inactive]/menu:rotate-0 group-[.inactive]/menu:opacity-100 transition-all duration-1000',
            )}
            role="none"
          />
        </div>
        <div className="absolute top-0 left-0">
          <XMarkIcon
            className="h-6 w-6 text-base-text group-[.active]/menu:rotate-0 group-[.active]/menu:opacity-100 group-[.inactive]/menu:rotate-90 group-[.inactive]/menu:opacity-0 transition-all duration-1000"
            role="none"
          />
        </div>
        <span className="sr-only">Toggle mobile menu</span>
      </div>
    </>
  )

  useEffect(() => {
    setMenuActive((prevState) => (props.searchActive === true || props.ctasActive === true ? false : prevState))
  }, [props.searchActive, props.ctasActive, props.menuActive])

  // Lock body scroll while the mobile menu is open so the page can't scroll behind it.
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (!menuActive) return
    const { body, documentElement } = document
    const prevBody = body.style.overflow
    const prevHtml = documentElement.style.overflow
    body.style.overflow = 'hidden'
    documentElement.style.overflow = 'hidden'
    return () => {
      body.style.overflow = prevBody
      documentElement.style.overflow = prevHtml
    }
  }, [menuActive])

  useEffect(() => {
    if (props.type === 'mobile') {
      document.addEventListener('click', (evt) => {
        if (
          !evt.target.closest('[data-type="mobileNavTrigger"]') &&
          !evt.target.closest('[data-type="mobile-navigation"]') &&
          !evt.target.closest('[data-type="mobile-megamenu-navigation"]') &&
          !evt.target.closest('[data-type="mobile-nav-search"]') &&
          !evt.target.closest('[data-type="cta-dropdown"]')
        ) {
          setMenuActive(false)
        }
      })
    }
  }, [])

  const handleClick = () => {
    setMenuActive((prevState) => {
      const isActive = !prevState
      props.setMenuActive(isActive)
      props.setSearchActive((prevState) => {
        let newState = prevState
        if (isActive === true) {
          newState = false
        }
        props.setCtasActive(newState)
        return newState
      })
      return isActive
    })
  }

  return (
    <>
      <div
        data-type="mobileNavTrigger"
        className={`peer/menutrigger ${menuActive && (props.searchActive === false || props.searchActive === undefined) ? 'active' : 'inactive'} xl:hidden ml-3`}
      >
        <ButtonEl theme={'none'} className="px-0! shadow-none! h-full items-center" onClick={handleClick}>
          {content}
        </ButtonEl>
      </div>

      {typeof document !== 'undefined' &&
        createPortal(
          <div
            data-type="mobile-navigation-wrapper"
            className={`
              bg-(--color-white) flex-col justify-between xl:hidden
              ${menuActive ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}
              ${hasTopNav ? 'lg:!top-[112px] lg:!h-[calc(100vh-112px)]' : ''}
            `}
          >
            {/* Search band — mirrors Figma design; always visible when menu is open */}
            <div data-type="mobile-nav-search" className="w-full shrink-0 bg-(--sb-search-box-bg) px-6 py-4">
              <SearchForm
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                onSubmit={submitForm}
                isActive={menuActive}
                formId="mobile-nav-search"
                placeholder="Search term"
                formClassName="w-full"
                innerClassName="!gap-2 min-w-0"
                buttonClassName="!px-3 text-white"
              />
            </div>

            {/* Scrollable container for the menu items */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain w-full py-10">
              {hasDataSourcedNav ? (
                <MobileMegaMenuNav {...props} megaMenuColumns={props.megaMenuColumns} menuActive={menuActive} />
              ) : (
                <MainNav {...props} nav={props.nav} type={props.type} stub="mob" />
              )}

              {/* Top navigation bar links — shown below main nav on mobile */}
              {hasTopNav && (
                <div className="w-full px-6 pt-4 gap-2 flex flex-col">
                  {topNavBar.map((navItem, idx) => (
                    <a
                      key={`mobile-topnav-${idx}`}
                      href={navItem?.link?.url}
                      target={navItem?.link?.target}
                      className="py-4 font-medium leading-6 text-(--sb-mega-menu-mobile-list-text)! hover:text-(--sb-mega-menu-mobile-list-text-hover) transition-colors"
                      onClick={() => {
                        dataLayerPush({
                          event: 'navigation_menu_click',
                          category: navItem.link?.name,
                          click_text: navItem.link?.name,
                        })
                      }}
                    > 
                      {navItem.link?.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* {props.ctas && (
              <div className="w-full shrink-0 px-4 py-8">
                <CtaDropDown
                  {...props}
                  type="button"
                  variant="divider"
                  position="mobileNav"
                  className="h-[60px] bg-(--color-button-primary-bg) text-(--color-white) transition-opacity hover:opacity-90"
                />
              </div>
            )} */}

            <div className="flex flex-wrap items-center justify-end py-2 px-6 gap-2 shrink-0 border-t border-(--color-border-tertiary) bg-(--sb-mobile-nav-bg) shadow-[0_0_4px_0_rgba(0,0,0,0.25)]">
              {isAuthenticated ? (
                <>
                  <div className="relative inline-flex">
                    <ButtonEl
                      theme="primary"
                      link={{ url: myProfileUrl, name: 'My Profile' }}
                      className={`!px-[14px] !py-[10px]`}
                      onClick={() => {
                        dataLayerPush({
                          event: 'navigation_menu_click',
                          category: 'My Profile',
                          click_text: 'My Profile',
                        })
                      }}
                    />
                    {needsRenewal && (
                      <a
                        href="/member/renewals"
                        className="absolute -top-2 -right-2 inline-flex items-center justify-center w-6 h-6 bg-white hover:bg-[color-mix(in_oklch,var(--color-nds-orange)_10%,white)] border-2 border-nds-orange rounded-full transition-colors text-nds-orange!"
                        aria-label="Membership renewal required — click to renew"
                      >
                        <span className="text-sm font-bold leading-none text-nds-orange!" role="status">!</span>
                      </a>
                    )}
                  </div>
                  <form action={LOGOUT_URL} method="post" className="inline-flex">
                    <input
                      type="hidden"
                      name="__RequestVerificationToken"
                      value={getCsrfToken()}
                    />
                    <input type="hidden" name="returnUrl" value={getReturnUrl()} />
                    <ButtonEl
                      type="submit"
                      theme="secondary"
                      className={`!px-[14px] !py-[10px] !text-(--color-border-button-secondary-text) hover:!text-(--color-button-secondary-hover-text)`}
                      onClick={() => {
                        dataLayerPush({
                          event: 'logout_intent',
                          click_text: 'Logout',
                          page_section: 'Header',
                        })
                      }}
                    >
                      Logout
                    </ButtonEl>
                  </form>
                </>
              ) : (
                <>
                  {joinLinkData && (
                    <ButtonEl
                      icon={joinLinkData.linkIconClass}
                      position={joinLinkData.linkPosition}
                      theme="secondary"
                      target={joinLinkData.link?.target}
                      link={joinLinkData.link}
                      className={`!px-[14px] !py-[10px] !text-(--color-border-button-secondary-text) hover:!text-(--color-button-secondary-hover-text)`}
                      skipCtaClickTracking
                      onClick={() => {
                        dataLayerPush({
                          event: 'signup_intent',
                        })
                      }}
                    />
                  )}
                  {loginLinkData && (
                    <ButtonEl
                      icon={loginLinkData.linkIconClass}
                      position={loginLinkData.linkPosition || 'After'}
                      theme="primary"
                      target={loginLinkData.link?.target}
                      link={loginLinkData.link}
                      className={`!px-[14px] !py-[10px]`}
                      skipCtaClickTracking
                      onClick={() => {
                        dataLayerPush({
                          event: 'login_intent',
                          click_text: loginLinkData.link?.name || 'Login',
                          page_section: 'Header',
                          category: null,
                          member_only: false,
                        })
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

export default MobileNav
